/**
 * Payouts Tab Component
 * Manages partner commission payouts
 */
import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Wallet, Loader2 } from 'lucide-react';
import { requestPartnerPayout } from '../../lib/api';
import { toast } from 'sonner';

const PayoutsTab = ({ analytics, payouts, onPayoutRequested }) => {
  const [showRequestPayout, setShowRequestPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const availableBalance = (analytics?.total_commission_earned || 0) - (analytics?.pending_commission || 0);

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount < 50) {
      toast.error('Minimum payout is $50');
      return;
    }
    
    setSubmitting(true);
    try {
      await requestPartnerPayout({ amount, currency: 'USD' });
      toast.success('Payout request submitted');
      setShowRequestPayout(false);
      setPayoutAmount('');
      onPayoutRequested?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 mt-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-heading font-semibold">Commission Payouts</h2>
          <p className="text-sm text-muted-foreground">
            Available balance: ${availableBalance.toFixed(2)}
          </p>
        </div>
        <Dialog open={showRequestPayout} onOpenChange={setShowRequestPayout}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" data-testid="request-payout-btn">
              <Wallet className="w-4 h-4 mr-2" />
              Request Payout
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Request Commission Payout</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted text-center">
                <p className="text-sm text-muted-foreground">Available Commission</p>
                <p className="text-2xl font-bold">
                  ${availableBalance.toFixed(2)}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Payout Amount (min $50)</Label>
                <Input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="50.00"
                  min="50"
                  className="rounded-xl"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleRequestPayout} disabled={submitting} className="w-full rounded-xl">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request Payout'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {payouts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Wallet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No payouts yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {payouts.map((payout) => (
            <Card key={payout.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-lg">${payout.amount.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className={payout.status === 'completed' ? 'bg-emerald-500/20 text-emerald-600' : 'bg-amber-500/20 text-amber-600'}>
                    {payout.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PayoutsTab;
